import { motion } from "framer-motion";
import { useSiteImages } from "@/hooks/useSiteImages";

const pillars = [
  { title: "Innovation & Research", desc: "Pioneering atmospheric water craft and gentle purification methods behind the world's most consistently pure water." },
  { title: "Global Partnerships", desc: "Bringing pure air water to markets worldwide, from luxury hospitality to conscious everyday hydration." },
  { title: "Environmental Strategy", desc: "Every decision — energy, packaging, facility design — leaves the earth better than we found it." },
  { title: "Quality & Craft", desc: "Mineral precision and verified quality in every bottle, exactly what nature intended." },
];

export const Vision = () => {
  const imgs = useSiteImages();
  return (
  <section id="vision" data-testid="vision-section" className="py-24 md:py-32 lg:py-40 bg-[#F5F5F7]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-3xl mx-auto"
      >
        <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">Where We Are Going</p>
        <h2 data-testid="vision-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-medium text-[#1D1D1F]">
          A future where pure water belongs to everyone.
        </h2>
        <p className="mt-8 text-lg md:text-xl leading-relaxed text-[#86868B]">
          We envision a world where access to pure water no longer depends on where you are
          born or how deep a well can be dug. By making water from air — nature's most
          renewable source — Nyla is building toward a future where the most essential
          resource on earth is available to all. Every bottle we craft leaves the ground
          untouched, the rivers flowing, and the wells full for the communities that need
          them most.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 1.04 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.1 }}
        className="mt-16 md:mt-24 rounded-3xl overflow-hidden relative"
      >
        <img
          src={imgs.vision_kingfisher}
          alt="The Jacobin Cuckoo — Nyla's guiding influence"
          loading="lazy"
          decoding="async"
          className="w-full h-[380px] md:h-[520px] object-cover"
        />
        <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs tracking-[0.2em] uppercase font-semibold text-white/90 bg-black/30 backdrop-blur-md px-5 py-2.5 rounded-full">
          The Jacobin Cuckoo — Our Guiding Influence
        </p>
      </motion.div>

      <div className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        {pillars.map((p, i) => (
          <motion.div
            key={p.title}
            data-testid={`team-pillar-${i}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-300 ease-out"
          >
            <h3 className="font-display text-lg md:text-xl tracking-tight font-medium text-[#1D1D1F] mb-3">{p.title}</h3>
            <p className="text-sm leading-relaxed text-[#86868B]">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};
